/**
 * What the navigation is made of, apart from how it is drawn.
 *
 * Split out of AppLayout so the rule about who sees what can be asserted
 * against directly. The layout is a component with a header, a company picker
 * and two watchers in it; the answer to "does an owner see Reminders" is a
 * pure function of a role, and pulling it out is what lets a test ask.
 */
import {
  BarChart3,
  RotateCcw,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarSearch,
  ClipboardList,
  Clock,
  Download,
  HandCoins,
  IdCard,
  IndianRupee,
  LayoutDashboard,
  ListPlus,
  Phone,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Table2,
  UserCheck,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';
import { canSeeAllCompanies, isCompanyAdmin, isStaff, isSupervisor } from '../store/auth';
import type { User } from '../types';

/**
 * The tab bar, grouped.
 *
 * Every item keeps the visibility flag it already had, unchanged. Nothing here
 * introduces a second permission rule: a group renders only when at least one
 * item inside it survives the same filter the flat bar used, so a recruiter
 * sees no Team, Admin or Payroll group because every item in them filtered
 * out — not because a group-level check says so. A rule written twice is a rule
 * that eventually disagrees with itself, and this one decides who sees payroll.
 *
 * Items are never rendered disabled. A control you cannot use is an invitation
 * to try, and the route guards in App.tsx refuse anyway.
 */
export type NavItem = {
  to: string;
  label: string;
  icon: typeof ClipboardList;
  end?: boolean;
  /** Hidden from an employee: recruitment work they have no part in. */
  staffOnly?: boolean;
  /** The owner alone — adding a form field is nearer a schema change. */
  ownerOnly?: boolean;
  supervisorOnly?: boolean;
  adminOnly?: boolean;
  /**
   * Hidden from the owner, who can otherwise see everything.
   *
   * The only flag here that subtracts. Every other one is a floor — owner,
   * admin, supervisor, staff — and the owner clears all of them, so without a
   * flag that takes something away there is no way to express "everyone but
   * the owner".
   *
   * Decluttering, not permission. The routes stay reachable by URL and the
   * server rule behind them is unchanged: these screens are staff-and-above,
   * and the owner is staff. An owner who wants one types the address.
   */
  hideFromOwner?: boolean;
};

/**
 * A dropdown, or a single link when `asTab`.
 *
 * `hideFromOwner` exists at this level as well as on the item because three of
 * the four things hidden from the owner are whole groups. Doing it by flagging
 * every item inside would hide the group by side effect — it disappears
 * because it emptied — and would then need the flag repeated on any item added
 * later, which is exactly the rule-written-twice problem above.
 */
export type NavGroup = {
  label: string;
  items: NavItem[];
  asTab?: boolean;
  hideFromOwner?: boolean;
};

/** Always on the bar, in the order the day runs. */
export const TOP_LEVEL: NavItem[] = [
  { to: '/', label: 'My Day', icon: ClipboardList, end: true, hideFromOwner: true },
  { to: '/calls', label: 'Daily Calls Report', icon: Phone, staffOnly: true },
  { to: '/candidates', label: 'Interview Candidates', icon: Users, staffOnly: true },
  { to: '/tracker', label: 'Joining Tracker', icon: Table2, staffOnly: true },
  { to: '/clients', label: 'Clients', icon: Building2, staffOnly: true },
  // Owner only, so nobody else's bar changes. Last because it is the last
  // thing that happens: call, submit, place, then bill.
  { to: '/admin/invoices', label: 'Invoices', icon: Receipt, ownerOnly: true },
];

/**
 * `asTab` renders the group as one plain link instead of a dropdown.
 *
 * It exists so a single destination can sit among the dropdowns rather than
 * being either buried in one or exiled to the far left with the top-level
 * items. A menu that opens to reveal exactly one thing is a menu that wastes
 * a click.
 *
 * Nothing sets it at the moment. Job Postings was the only group that did,
 * and it has been removed; the flag is kept because the next single-item
 * group will want it and because both shells still honour it.
 */
export const GROUPS: NavGroup[] = [
  {
    label: 'My Work',
    hideFromOwner: true,
    items: [
      { to: '/attendance', label: 'Attendance', icon: CalendarCheck },
      { to: '/leave', label: 'Leave', icon: CalendarDays },
      { to: '/timesheets', label: 'Timesheets', icon: Clock },
      { to: '/payslips', label: 'Payslips', icon: Receipt },
      { to: '/profile', label: 'My Profile', icon: IdCard },
    ],
  },
  {
    label: 'Reminders',
    hideFromOwner: true,
    items: [
      { to: '/interviews', label: 'Interviews', icon: CalendarClock, staffOnly: true },
      { to: '/offers', label: 'Offers', icon: HandCoins, staffOnly: true },
      { to: '/placements', label: 'Placements', icon: UserCheck, staffOnly: true },
    ],
  },
  {
    label: 'Team',
    items: [
      { to: '/team', label: 'Team', icon: BarChart3, supervisorOnly: true },
      {
        to: '/attendance-records',
        label: 'Attendance records',
        icon: CalendarSearch,
        supervisorOnly: true,
      },
      { to: '/admin/users', label: 'Users', icon: UserCog, supervisorOnly: true },
      // Company-admin and owner only: reviewing a submission means seeing full
      // bank, PAN and Aadhaar, which a reporting line is the wrong boundary for.
      {
        to: '/admin/profile-reviews',
        label: 'Profile Reviews',
        icon: UserCheck,
        adminOnly: true,
      },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/admin/billing', label: 'Billing', icon: IndianRupee, supervisorOnly: true },
      { to: '/admin/exports', label: 'Exports', icon: Download, supervisorOnly: true },
      { to: '/admin/workplaces', label: 'Workplaces', icon: Settings, supervisorOnly: true },
      { to: '/admin/companies', label: 'Companies', icon: Building2, supervisorOnly: true },
      { to: '/admin/audit-logs', label: 'Audit trail', icon: ScrollText, adminOnly: true },
      // Where the candidates a destructive call outcome removed can be read
      // and put back. Admin-gated, matching the restore route.
      {
        to: '/admin/retired-candidates',
        label: 'Retired candidates',
        icon: RotateCcw,
        adminOnly: true,
      },
      {
        to: '/admin/custom-fields',
        label: 'Custom fields',
        icon: ListPlus,
        ownerOnly: true,
      },
      // Not in the grouping brief, and dropping it would strand a working,
      // route-guarded page. Company-admin gated exactly as before.
      { to: '/admin/overview', label: 'Overview', icon: LayoutDashboard, adminOnly: true },
    ],
  },
  {
    label: 'Payroll',
    items: [
      { to: '/admin/payroll', label: 'Payroll', icon: Wallet, adminOnly: true },
      { to: '/admin/pay-setup', label: 'Pay setup', icon: ShieldCheck, adminOnly: true },
    ],
  },
];

/** The flat bar's filter, still the only one. */
export function visibleTo(item: NavItem, user: User | null): boolean {
  // Before the role flags, not after. This subtracts from a role that clears
  // every floor below, so a check placed after them would sit behind a
  // `return` that has already fired.
  if (item.hideFromOwner && canSeeAllCompanies(user)) return false;
  if (item.ownerOnly) return canSeeAllCompanies(user);
  if (item.adminOnly) return isCompanyAdmin(user);
  if (item.supervisorOnly) return isSupervisor(user);
  return !item.staffOnly || isStaff(user);
}

/**
 * The whole bar as one role sees it — the layout renders exactly this.
 *
 * Returned rather than filtered at the call site so the test and the screen
 * are looking at the same thing. A test that reimplemented the filtering would
 * pass while the bar was wrong.
 */
export function navFor(user: User | null): {
  topLevel: NavItem[];
  groups: NavGroup[];
} {
  return {
    topLevel: TOP_LEVEL.filter((item) => visibleTo(item, user)),
    groups: GROUPS
      .filter((group) => !(group.hideFromOwner && canSeeAllCompanies(user)))
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => visibleTo(item, user)),
      }))
      // An empty group is not a heading with nothing under it — it is absent.
      .filter((group) => group.items.length > 0),
  };
}
